import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  Shield, ArrowRight, Check, Star, Clock, BadgeCheck, 
  FileText, Award, Eye, Briefcase, TrendingUp, Send,
  ChevronDown, Lock, Sparkles, User, CheckCircle
} from 'lucide-react';

const ProfessionalsPage = () => {
  const [heroVisible, setHeroVisible] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    setTimeout(() => setHeroVisible(true), 100);
  }, []);

  const freeFeatures = [
    { icon: Shield, title: 'VETT ID', desc: 'Your unique professional identifier that travels with you forever.' },
    { icon: FileText, title: 'VETT Ledger', desc: 'Your portable, verified work history owned by you, not your employers.' },
    { icon: Clock, title: 'Career Timeline', desc: 'Visual timeline of your career, exportable and shareable.' },
    { icon: Send, title: 'Request Records', desc: 'Ask past employers to create VETT records for your work history.' },
    { icon: BadgeCheck, title: 'VETT Tier', desc: 'Build your tier status from Unverified to Distinguished through verified records.' },
    { icon: Eye, title: 'Visibility Controls', desc: 'Control who sees your profile: Open, Selective, or Private.' },
  ];

  const proFeatures = [
    { title: 'Priority Featuring', desc: 'Appear higher in employer search results' },
    { title: 'Score Tips', desc: 'Get personalized tips to improve your VETT tier' },
    { title: 'Analytics', desc: 'See who viewed your profile and when' },
    { title: 'PDF Export', desc: 'Export your VETT Ledger as a professional PDF' },
  ];

  const tiers = [
    { name: 'Basic', desc: 'Profile created + email verified + at least 1 record', color: '#86858d' },
    { name: 'Verified', desc: '1+ verified records + avg rating ≥3.5 + recent record within 3 years', color: '#5b8af0' },
    { name: 'Distinguished', desc: '3+ verified records from 2+ employers + avg rating ≥4.0 + all rehire-eligible', color: '#f0545f', featured: true },
    { name: 'Elite', desc: '5+ verified records from 3+ employers + avg rating ≥4.7 + leadership role + 7+ year career', color: '#f0a030', glow: true },
  ];

  const faqs = [
    { q: 'Is it really free forever?', a: 'Yes. Your VETT ID, VETT Ledger, career timeline, and ability to request records are free forever. Pro upgrade ($9.90/month) adds priority featuring, analytics, and PDF export.' },
    { q: 'Who can see my records?', a: 'You control visibility. Open means any verified employer can see your full profile. Selective lets you approve access requests. Private hides you from all searches.' },
    { q: 'What if I disagree with a record?', a: 'You must acknowledge each record before it becomes verified. If you disagree, you can flag specific sections for discussion with your employer before signing.' },
    { q: 'Can I request records from past employers?', a: 'Yes. Send a request to any past employer, even if they\'re not on VETT. They\'ll receive instructions to create your record. You can have up to 5 pending requests.' },
    { q: 'What\'s the difference between tiers?', a: 'Four tiers indicate verification depth: Unverified (score 0-19, verification in progress), Verified (20-49, identity confirmed), Established (50-74, multiple records verified), Distinguished (75-100, comprehensive verification history).' },
    { q: 'Do I keep my records if I change jobs?', a: 'Yes. Your VETT Ledger is yours forever. Records from every employer stay with you, building your verified career history over time.' },
  ];

  return (
    <div className="vett-page min-h-screen overflow-x-hidden" style={{ backgroundColor: 'var(--vett-navy)' }}>
      {/* Skip link */}
      <a href="#main-content" className="vett-skip-link">Skip to main content</a>
      
      {/* Noise texture */}
      <div className="vett-noise" aria-hidden="true" />
      
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl" style={{ 
        backgroundColor: 'rgba(14, 17, 24, 0.85)',
        borderBottom: '1px solid var(--vett-border)',
        height: 'var(--vett-nav-height)'
      }}>
        <div className="vett-container px-6">
          <div className="flex justify-between items-center h-[68px]">
            <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ 
                background: 'var(--vett-coral-gradient)',
                boxShadow: 'var(--vett-shadow-glow)'
              }}>
                <Shield className="h-5 w-5 text-white" />
              </div>
              <span className="vett-display text-xl" style={{ color: 'var(--vett-white)' }}>VETT</span>
            </Link>
            
            <nav className="hidden md:flex items-center gap-8">
              <Link to="/employers" className="vett-mono text-sm hover:opacity-100 transition-opacity" style={{ 
                color: 'var(--vett-white-dim)',
                letterSpacing: 'var(--vett-tracking-wide)'
              }}>For Employers</Link>
              <Link to="/professionals" className="vett-mono text-sm" style={{ 
                color: 'var(--vett-coral)',
                letterSpacing: 'var(--vett-tracking-wide)'
              }}>For Professionals</Link>
              <Link to="/#pricing" className="vett-mono text-sm hover:opacity-100 transition-opacity" style={{ 
                color: 'var(--vett-white-dim)',
                letterSpacing: 'var(--vett-tracking-wide)'
              }}>Pricing</Link>
            </nav>

            <div className="flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost" className="vett-btn-ghost rounded-md" data-testid="login-btn">
                  Sign in
                </Button>
              </Link>
              <Link to="/signup?type=employee">
                <Button className="vett-btn vett-btn-primary" data-testid="signup-btn">
                  Join Free
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section id="main-content" className="pt-32 pb-20 px-6 relative">
        {/* Glow effect */}
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[600px] h-[400px] pointer-events-none" style={{
          background: 'var(--vett-coral-glow)',
          filter: 'blur(100px)',
          borderRadius: '50%'
        }} aria-hidden="true" />
        
        <div className="vett-container relative z-10">
          <div className="flex flex-col lg:flex-row items-center gap-16">
            {/* Left side - Hero copy */}
            <div className="flex-1 text-center lg:text-left">
              {/* Eyebrow */}
              <div className={`vett-eyebrow mb-8 justify-center lg:justify-start transition-all duration-700 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                For Professionals
              </div>
              
              <h1 className={`vett-display text-5xl sm:text-6xl lg:text-7xl mb-8 transition-all duration-700 delay-100 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`} style={{ color: 'var(--vett-white)' }}>
                Own your <em>career story</em>
              </h1>

              <p className={`vett-accent text-lg lg:text-xl mb-10 max-w-xl transition-all duration-700 delay-200 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`} style={{ color: 'var(--vett-white-dim)' }}>
                Build a verified work history that's yours forever. No more self-written resumes. 
                Let your employers vouch for you, permanently.
              </p>

              <div className={`flex flex-col sm:flex-row gap-4 justify-center lg:justify-start transition-all duration-700 delay-300 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
                <Link to="/signup?type=employee">
                  <Button className="vett-btn vett-btn-primary h-14 px-8" data-testid="hero-cta-join">
                    Join Free Forever
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </div>

              <p className={`vett-mono text-xs mt-6 text-center lg:text-left transition-all duration-700 delay-400 ${heroVisible ? 'opacity-100' : 'opacity-0'}`} style={{ 
                color: 'var(--vett-gray-light)',
                letterSpacing: 'var(--vett-tracking-wide)'
              }}>
                Free forever. No credit card ever.
              </p>
            </div>

            {/* Right side - Tier Visualization */}
            <div className={`flex-shrink-0 transition-all duration-700 delay-500 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
              <div className="vett-card p-6 w-[300px] sm:w-[340px]" data-testid="tier-visualization">
                <h3 className="vett-label text-center mb-6">VETT Tier System</h3>
                <div className="space-y-4">
                  {tiers.map((tier, i) => (
                    <div 
                      key={i} 
                      className="flex items-center gap-4 p-3 rounded-lg relative" 
                      style={{
                        backgroundColor: 'var(--vett-surface-2)',
                        borderTop: tier.featured ? '2px solid #f0545f' : 'none'
                      }}
                    >
                      <div 
                        className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" 
                        style={{
                          backgroundColor: `${tier.color}20`,
                          boxShadow: tier.glow ? '0 0 20px rgba(240, 84, 95, 0.4)' : 'none'
                        }}
                      >
                        <Award className="w-5 h-5" style={{ color: tier.color }} />
                      </div>
                      <div>
                        <h4 style={{ 
                          fontFamily: 'var(--vett-font-display)', 
                          fontWeight: 700,
                          fontSize: '14px',
                          color: tier.color 
                        }}>{tier.name}</h4>
                        <p style={{ 
                          fontFamily: 'var(--vett-font-mono)', 
                          fontWeight: 300,
                          fontSize: '10px',
                          color: 'var(--vett-gray-light)' 
                        }}>{tier.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Free Features */}
      <section className="vett-section" style={{ backgroundColor: 'var(--vett-navy-2)' }}>
        <div className="vett-container">
          <div className="text-center mb-16">
            <span className="vett-pill vett-pill-green mb-6 inline-block">FREE FOREVER</span>
            <h2 className="vett-display text-3xl sm:text-4xl lg:text-5xl mb-4" style={{ color: 'var(--vett-white)' }}>
              Everything you need, <em>free</em>
            </h2>
            <p className="vett-accent text-lg max-w-xl mx-auto" style={{ color: 'var(--vett-white-dim)' }}>
              Build your verified career history without paying a cent.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {freeFeatures.map((feature, i) => (
              <div key={i} className="vett-card p-6" data-testid={`free-feature-${i}`}>
                <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4" style={{
                  backgroundColor: 'var(--vett-green-dim)'
                }}>
                  <feature.icon className="h-6 w-6" style={{ color: 'var(--vett-green)' }} />
                </div>
                <h3 className="vett-display text-base mb-2" style={{ color: 'var(--vett-white)' }}>{feature.title}</h3>
                <p className="vett-mono text-xs" style={{ color: 'var(--vett-gray-light)', lineHeight: 'var(--vett-leading-mono)' }}>{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pro Upgrade */}
      <section className="vett-section">
        <div className="vett-container max-w-4xl">
          <div className="vett-card p-8 lg:p-12" style={{
            background: 'linear-gradient(135deg, var(--vett-coral-dim), var(--vett-surface))',
            borderColor: 'var(--vett-border-active)'
          }}>
            <div className="flex flex-col lg:flex-row items-center gap-8">
              <div className="flex-1 text-center lg:text-left">
                <span className="vett-pill vett-pill-coral mb-4 inline-block">PRO UPGRADE</span>
                <h2 className="vett-display text-3xl sm:text-4xl mb-4" style={{ color: 'var(--vett-white)' }}>
                  Stand out to employers
                </h2>
                <p className="vett-accent text-lg mb-6" style={{ color: 'var(--vett-white-dim)' }}>
                  $9.90/month for priority featuring and advanced tools.
                </p>
                <ul className="space-y-3 mb-8">
                  {proFeatures.map((feature, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <Check className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: 'var(--vett-coral)' }} />
                      <div>
                        <span className="vett-display text-sm block" style={{ color: 'var(--vett-white)' }}>{feature.title}</span>
                        <span className="vett-mono text-xs" style={{ color: 'var(--vett-gray-light)' }}>{feature.desc}</span>
                      </div>
                    </li>
                  ))}
                </ul>
                <Link to="/signup?type=employee">
                  <Button className="vett-btn vett-btn-primary h-14 px-8" data-testid="pro-cta">
                    Start Free, Upgrade Later
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </div>
              <div className="flex-shrink-0">
                <div className="text-center">
                  <div className="vett-display text-5xl mb-2" style={{ color: 'var(--vett-white)' }}>$9.90</div>
                  <div className="vett-mono text-xs" style={{ color: 'var(--vett-gray-light)' }}>/month</div>
                  <div className="vett-mono text-xs mt-2" style={{ color: 'var(--vett-gray)' }}>Cancel anytime</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="vett-section" style={{ backgroundColor: 'var(--vett-navy-2)' }}>
        <div className="vett-container max-w-4xl">
          <div className="text-center mb-16">
            <p className="vett-eyebrow justify-center mb-6">Getting Started</p>
            <h2 className="vett-display text-3xl sm:text-4xl lg:text-5xl" style={{ color: 'var(--vett-white)' }}>
              Build your <em>VETT Ledger</em>
            </h2>
          </div>

          <div className="space-y-8">
            {[
              { step: '01', title: 'Create your account', desc: 'Sign up free with your email. Get your VETT ID instantly. Complete your profile.' },
              { step: '02', title: 'Request records from past employers', desc: 'Send requests to former managers or HR. They\'ll receive instructions to create your record.' },
              { step: '03', title: 'Acknowledge and sign', desc: 'Review each record. Flag any disagreements for discussion. Sign to make it verified.' },
              { step: '04', title: 'Build your tier', desc: 'Each verified record from a different employer builds your tier. Distinguished tier = maximum trust.' },
            ].map((item, i) => (
              <div key={i} className="flex gap-6 items-start">
                <div className="vett-display text-4xl flex-shrink-0" style={{ color: 'var(--vett-coral-dim)' }}>{item.step}</div>
                <div className="pt-1">
                  <h3 className="vett-display text-lg mb-1" style={{ color: 'var(--vett-white)' }}>{item.title}</h3>
                  <p className="vett-mono text-xs" style={{ color: 'var(--vett-gray-light)' }}>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Visibility Controls */}
      <section className="vett-section">
        <div className="vett-container max-w-4xl">
          <div className="text-center mb-12">
            <p className="vett-eyebrow justify-center mb-6">Privacy</p>
            <h2 className="vett-display text-3xl sm:text-4xl lg:text-5xl mb-4" style={{ color: 'var(--vett-white)' }}>
              You control who <em>sees you</em>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              { name: 'Open', desc: 'Full profile visible to any verified employer with an active VETT account.', icon: Eye, color: 'var(--vett-green)' },
              { name: 'Selective', desc: 'Basic info visible. Employers must request access for full details. You approve.', icon: Lock, color: 'var(--vett-amber)' },
              { name: 'Private', desc: 'Hidden from all searches. Build your ledger privately until you\'re ready.', icon: Shield, color: 'var(--vett-gray-light)' },
            ].map((mode, i) => (
              <div key={i} className="vett-card p-6 text-center" data-testid={`visibility-${mode.name.toLowerCase()}`}>
                <div className="w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-4" style={{
                  backgroundColor: `${mode.color}20`
                }}>
                  <mode.icon className="h-6 w-6" style={{ color: mode.color }} />
                </div>
                <h3 className="vett-display text-lg mb-2" style={{ color: mode.color }}>{mode.name}</h3>
                <p className="vett-mono text-xs" style={{ color: 'var(--vett-gray-light)' }}>{mode.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="vett-section" style={{ backgroundColor: 'var(--vett-navy-2)' }}>
        <div className="vett-container max-w-3xl">
          <p className="vett-eyebrow justify-center mb-6">FAQ</p>
          <h2 className="vett-display text-3xl sm:text-4xl text-center mb-12" style={{ color: 'var(--vett-white)' }}>
            Questions from <em>professionals</em>
          </h2>

          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} className="vett-card overflow-hidden" data-testid={`faq-${i}`}>
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between p-6 text-left"
                >
                  <span className="vett-display text-sm pr-4" style={{ color: 'var(--vett-white)' }}>{faq.q}</span>
                  <ChevronDown 
                    className={`w-5 h-5 transition-transform flex-shrink-0 ${openFaq === i ? 'rotate-180' : ''}`} 
                    style={{ color: 'var(--vett-gray-light)' }}
                  />
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-6">
                    <p className="vett-mono text-xs" style={{ color: 'var(--vett-gray-light)', lineHeight: 'var(--vett-leading-mono)' }}>{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="vett-section">
        <div className="vett-container max-w-4xl text-center">
          <div className="vett-card p-12 lg:p-16" style={{
            background: 'linear-gradient(135deg, var(--vett-green-dim), var(--vett-surface))',
            borderColor: 'var(--vett-green-border)'
          }}>
            <span className="vett-pill vett-pill-green mb-6 inline-block">FREE FOREVER</span>
            <h2 className="vett-display text-3xl sm:text-4xl lg:text-5xl mb-4" style={{ color: 'var(--vett-white)' }}>
              Start building your <em>verified</em> career
            </h2>
            <p className="vett-accent text-lg mb-8 max-w-xl mx-auto" style={{ color: 'var(--vett-white-dim)' }}>
              Join thousands of professionals who own their work history.
            </p>
            <Link to="/signup?type=employee">
              <Button className="vett-btn vett-btn-primary h-14 px-8" data-testid="final-cta">
                Create Your Free Account
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={{ borderTop: '1px solid var(--vett-border)' }} className="py-12 px-6">
        <div className="vett-container">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
            <div className="col-span-2 md:col-span-1">
              <Link to="/" className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'var(--vett-coral-gradient)' }}>
                  <Shield className="h-4 w-4 text-white" />
                </div>
                <span className="vett-display text-lg" style={{ color: 'var(--vett-white)' }}>VETT</span>
              </Link>
              <p className="vett-mono text-xs" style={{ color: 'var(--vett-gray-light)' }}>
                Verified work history for the modern workforce.
              </p>
            </div>
            
            <div>
              <h4 className="vett-label mb-4">Product</h4>
              <ul className="space-y-2">
                <li><Link to="/employers" className="vett-mono text-xs hover:opacity-100 transition-opacity" style={{ color: 'var(--vett-white-dim)' }}>For Employers</Link></li>
                <li><Link to="/professionals" className="vett-mono text-xs" style={{ color: 'var(--vett-coral)' }}>For Professionals</Link></li>
                <li><Link to="/#pricing" className="vett-mono text-xs hover:opacity-100 transition-opacity" style={{ color: 'var(--vett-white-dim)' }}>Pricing</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="vett-label mb-4">Company</h4>
              <ul className="space-y-2">
                <li><Link to="/contact" className="vett-mono text-xs hover:opacity-100 transition-opacity" style={{ color: 'var(--vett-white-dim)' }}>Contact</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="vett-label mb-4">Legal</h4>
              <ul className="space-y-2">
                <li><Link to="/privacy" className="vett-mono text-xs hover:opacity-100 transition-opacity" style={{ color: 'var(--vett-white-dim)' }}>Privacy</Link></li>
                <li><Link to="/terms" className="vett-mono text-xs hover:opacity-100 transition-opacity" style={{ color: 'var(--vett-white-dim)' }}>Terms</Link></li>
                <li><Link to="/impressum" className="vett-mono text-xs hover:opacity-100 transition-opacity" style={{ color: 'var(--vett-white-dim)' }}>Impressum</Link></li>
              </ul>
            </div>
          </div>
          
          <div className="pt-8" style={{ borderTop: '1px solid var(--vett-border)' }}>
            <p className="vett-mono text-xs text-center" style={{ color: 'var(--vett-gray)' }}>
              © {new Date().getFullYear()} VETT. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ProfessionalsPage;
